import React, { useEffect, useMemo, useRef, useState } from "react";
import LeaseUpCalculator from "./LeaseUpCalculator";
import { useCompareHover } from "../modals/CompareHoverContext";

// ---------- helpers ----------
const formatMoney = (v, maxFrac = 2) => {
  const s = String(v ?? "");
  const cleaned = s.replace(/[$,\s]/g, "");
  if (cleaned === "" || cleaned === "-") return cleaned;
  const neg = cleaned.startsWith("-");
  let body = neg ? cleaned.slice(1) : cleaned;
  const endsWithDot = /\.\s*$/.test(cleaned);
  const firstDot = body.indexOf(".");
  const hasDot = firstDot !== -1;
  if (hasDot) body = body.slice(0, firstDot + 1) + body.slice(firstDot + 1).replace(/\./g, "");
  const [intRaw, fracRawAll] = body.split(".");
  const intNum = intRaw ? Number(intRaw) : 0;
  const intFmt = Number.isFinite(intNum) ? intNum.toLocaleString() : intRaw;
  if (maxFrac === 0) return `$${neg ? "-" : ""}${intFmt}`;
  let out = `$${neg ? "-" : ""}${intFmt}`;
  if (hasDot) {
    out += ".";
    if (!endsWithDot) out += (fracRawAll || "").slice(0, maxFrac);
  }
  return out;
};
const $0 = (v) => formatMoney(v, 0);
const formatPercent = (v, maxFrac = 3) => {
  const s = String(v ?? "");
  const cleaned = s.replace(/[%\s,]/g, "");
  if (cleaned === "" || cleaned === "-") return cleaned;
  const neg = cleaned.startsWith("-");
  let body = neg ? cleaned.slice(1) : cleaned;
  const endsWithDot = /\.\s*$/.test(cleaned);
  const firstDot = body.indexOf(".");
  const hasDot = firstDot !== -1;
  if (hasDot) body = body.slice(0, firstDot + 1) + body.slice(firstDot + 1).replace(/\./g, "");
  const [intRaw, fracRawAll] = body.split(".");
  const intNum = intRaw ? Number(intRaw) : 0;
  const intFmt = Number.isFinite(intNum) ? intNum.toLocaleString() : intRaw;
  let out = `${neg ? "-" : ""}${intFmt}`;
  if (hasDot) {
    out += ".";
    if (!endsWithDot) out += (fracRawAll || "").slice(0, maxFrac);
  }
  return `${out}%`;
};
const n = (v) => {
  if (v === "" || v == null || v === "-") return 0;
  const s = String(v).replace(/[$,%\s,]/g, "");
  const num = Number(s);
  return Number.isFinite(num) ? num : 0;
};
const nPct = (v) => {
  if (v == null || v === "") return 0;
  const str = String(v).trim();
  const num = n(v);
  return str.endsWith("%") ? num / 100 : num;
};
const uid = () => Math.random().toString(36).slice(2, 9);

// ---------- component ----------
export default function IncomeAdvanced({ data, onChange, onSave, packet, dataKey = "income" }) {
  const initial = (data && typeof data === "object" ? data[dataKey] : undefined) || {};
  const initLI = initial.line_items || {};
  const [incomeItems, setIncomeItems]   = useState(Array.isArray(initLI.income) ? initLI.income : []);
  const [lossItems, setLossItems]       = useState(Array.isArray(initLI.vcloss) ? initLI.vcloss : (Array.isArray(initLI.vacancy) ? initLI.vacancy : []));
  const [expenseItems, setExpenseItems] = useState(Array.isArray(initLI.expenses) ? initLI.expenses : []);

  // other shared fields (same as Basic)
  const [form, setForm] = useState({
    cap_mode: initial.cap_mode === "loaded" ? "loaded" : "unloaded",
    base_cap_rate: formatPercent(initial.base_cap_rate ?? initial.cap_rate ?? ""),
    effective_tax_rate: formatPercent(initial.effective_tax_rate ?? ""),
    deduction_type: initial.deduction_type || "none",
    deduction_amount: $0(initial.deduction_amount ?? ""),
    deduction_notes: initial.deduction_notes || "",
    lease_up: initial.lease_up || null,
    notes: initial.notes || "",
  });

  // Context (units/SF) from Cover (display only)
  const coverCtx = useMemo(() => {
    let latest = null;
    let maxOrder = -Infinity;
    for (const s of packet?.sections || []) {
      if (String(s?.type || "").toLowerCase() === "cover") {
        const oi = s?.order_index ?? 0;
        if (oi > maxOrder) { maxOrder = oi; latest = s; }
      }
    }
    const pi = latest?.data?.property_info || {};
    const unitType = pi.unit_type || "units"; // "units" | "SF" | "beds"
    const unitsOrSF = Number(String(pi.units ?? "").replace(/[$,%\s,]/g, "")) || 0;
    return { unitType, basisFromCover: unitsOrSF };
  }, [packet]);
  const UNITS   = useMemo(() => coverCtx.unitType === "SF" ? 0 : Math.max(0, Math.floor(coverCtx.basisFromCover || 0)), [coverCtx]);
  const AREA_SF = useMemo(() => coverCtx.unitType === "SF" ? (coverCtx.basisFromCover || 0) : 0, [coverCtx]);

  // totals
  const PGI   = useMemo(() => incomeItems.reduce((sum, it) => sum + n(it.amount), 0), [incomeItems]);
  const VCL   = useMemo(() => lossItems.reduce((sum, it) => sum + n(it.amount), 0), [lossItems]);
  const EGI   = useMemo(() => Math.max(0, PGI - VCL), [PGI, VCL]);
  const EXP   = useMemo(() => expenseItems.reduce((sum, it) => sum + n(it.amount), 0), [expenseItems]);
  const NOI   = useMemo(() => Math.max(0, EGI - EXP), [EGI, EXP]);

  const BASE_CAP = useMemo(() => nPct(form.base_cap_rate), [form.base_cap_rate]);
  const ETR      = useMemo(() => nPct(form.effective_tax_rate), [form.effective_tax_rate]);
  const APPLIED_CAP = useMemo(() => Math.max(0, BASE_CAP + (form.cap_mode === "loaded" ? ETR : 0)), [BASE_CAP, ETR, form.cap_mode]);
  const INDICATED_VALUE = useMemo(() => (APPLIED_CAP > 0 ? NOI / APPLIED_CAP : 0), [NOI, APPLIED_CAP]);

  const VAC_PCT = useMemo(() => PGI > 0 ? (VCL / PGI) * 100 : 0, [PGI, VCL]);

  // ✅ Gate the deduction by the selected type
  const ACTIVE_DEDUCTION = useMemo(() => {
    const manual = n(form.deduction_amount);
    const lease  = Number(form?.lease_up?.deduction) || 0;
    if (form.deduction_type === "manual")   return manual;
    if (form.deduction_type === "lease_up") return lease;
    return 0; // when "none" is selected
  }, [form.deduction_type, form.deduction_amount, form.lease_up]);

  // push up to PacketBuilder
  const lastPush = useRef("");
  useEffect(() => {
    const payload = {
      [dataKey]: {
        ui_mode: "advanced",

        // store line items
        line_items: {
          income: incomeItems,
          vcloss: lossItems,
          expenses: expenseItems,
        },

        // mirrors for Basic
        pgi_mode: "total",
        pgi_total: $0(PGI),
        pgi_per_unit: "",
        vac_mode: "dollar",
        vacancy: $0(VCL),
        exp_mode: "total",
        expenses_total: $0(EXP),
        expenses_pct: EGI > 0 ? formatPercent((EXP / EGI) * 100) : "",

        // cap/deductions/notes
        cap_mode: form.cap_mode,
        base_cap_rate: form.base_cap_rate,
        effective_tax_rate: form.effective_tax_rate,
        deduction_type: form.deduction_type,
        deduction_amount: form.deduction_amount,
        deduction_notes: form.deduction_notes,
        lease_up: form.lease_up,
        notes: form.notes,

        // read-only mirrors
        potential_gross: $0(PGI),
        expenses: $0(EXP),
        noi: $0(NOI),
        cap_rate: formatPercent(APPLIED_CAP * 100),
        indicated_value: $0(INDICATED_VALUE),

        // ✅ FIX: apply 0 when deduction_type === "none"
        deduction_applied: $0(ACTIVE_DEDUCTION),
        final_value: $0(Math.max(0, INDICATED_VALUE - ACTIVE_DEDUCTION)),

        _calc: { PGI, VAC: VCL, EGI, EXP, NOI, base_cap: BASE_CAP, effective_tax_rate: ETR, applied_cap: APPLIED_CAP },
      },
    };
    const sig = JSON.stringify(payload[dataKey]);
    if (sig !== lastPush.current) {
      onChange?.(payload);
      lastPush.current = sig;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(incomeItems),
    JSON.stringify(lossItems),
    JSON.stringify(expenseItems),
    form.cap_mode,
    form.base_cap_rate,
    form.effective_tax_rate,
    form.deduction_type,
    form.deduction_amount,
    form.deduction_notes,
    JSON.stringify(form.lease_up),
    form.notes,
    PGI, VCL, EGI, EXP, NOI, BASE_CAP, ETR, APPLIED_CAP, INDICATED_VALUE, ACTIVE_DEDUCTION
  ]);

  // UI
  return (
    <div className="space-y-6">
      {/* Context */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm flex flex-wrap items-center gap-3">
        <span className="text-slate-600">Context:</span>
        {coverCtx.unitType === "SF" ? (
          <Badge>Area (SF): <strong>{AREA_SF ? AREA_SF.toLocaleString() : "—"}</strong></Badge>
        ) : (
          <Badge>Units: <strong>{UNITS ? UNITS.toLocaleString() : "—"}</strong></Badge>
        )}
      </div>

      {/* Income line items */}
      <LineItemsTable
        title="Potential Gross Income — line items"
        items={incomeItems}
        onChange={setIncomeItems}
        addHint="Add Income Item"
      />
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
		  label="PGI (Total)"
		  value={$0(PGI)}
		  readOnly
		  onChange={() => {}}
		  compareKey="income.potential_gross"
		/>
      </div>

      {/* Vacancy & Credit Loss line items */}
      <LineItemsTable
        title="Vacancy & Credit Loss — line items"
        items={lossItems}
        onChange={setLossItems}
        addHint="Add V&C Loss Item"
      />
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
		  label="V&C Loss (Total)"
		  value={$0(VCL)}
		  readOnly
		  onChange={() => {}}
		  compareKey="income.vacancy"
		/>
        <Field label="V&C as % of PGI" value={PGI > 0 ? formatPercent((VCL / PGI) * 100) : ""} readOnly onChange={() => {}} />
      </div>

      {/* EGI */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
		  label="Effective Gross Income (EGI)"
		  value={$0(EGI)}
		  readOnly
		  onChange={() => {}}
		  compareKey="income.egi"
		/>
      </div>

      {/* Operating Expense line items */}
      <LineItemsTable
        title="Operating Expenses — line items"
        items={expenseItems}
        onChange={setExpenseItems}
        addHint="Add Expense Item"
      />
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
		  label="Opex (Total)"
		  value={$0(EXP)}
		  readOnly
		  onChange={() => {}}
		  compareKey="income.expenses"
		/>
        <Field label="Opex as % of EGI" value={EGI > 0 ? formatPercent((EXP / EGI) * 100) : ""} readOnly onChange={() => {}} />
      </div>

      {/* NOI (read-only) */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
		  label="Net Operating Income (NOI)"
		  value={$0(NOI)}
		  readOnly
		  onChange={() => {}}
		  compareKey="income.noi"
		/>
      </div>

      {/* Cap block */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Cap Mode</label>
          <Toggle
            options={[
              { key: "unloaded", label: "Unloaded" },
              { key: "loaded", label: "Loaded (Base + ETR)" },
            ]}
            value={form.cap_mode}
            onChange={(v) => setForm((prev) => ({ ...prev, cap_mode: v }))}
          />
        </div>
        <Field
          label="Base Cap Rate (%)"
          value={form.base_cap_rate}
          onChange={(v) => setForm((prev) => ({ ...prev, base_cap_rate: formatPercent(v) }))}
          placeholder="e.g., 7.25%"
          inputMode="decimal"
        />
        {form.cap_mode === "loaded" && (
          <Field
            label="Effective Tax Rate (%)"
            value={form.effective_tax_rate}
            onChange={(v) => setForm((prev) => ({ ...prev, effective_tax_rate: formatPercent(v) }))}
            placeholder="e.g., 2.20%"
            inputMode="decimal"
          />
        )}
      </div>

      {/* Deduction */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
        <div className="text-sm font-medium">Deduction (subtract from capitalized value)</div>
        <Toggle
          options={[
            { key: "none", label: "None" },
            { key: "manual", label: "Manual" },
            { key: "lease_up", label: "Lease-Up (NPV)" },
          ]}
          value={form.deduction_type}
          onChange={(v) => setForm((prev) => ({ ...prev, deduction_type: v }))}
        />
        {form.deduction_type === "manual" && (
          <div className="grid sm:grid-cols-3 gap-3">
            <Field
              label="Description / Notes"
              value={form.deduction_notes}
              onChange={(v) => setForm((prev) => ({ ...prev, deduction_notes: v }))}
              placeholder="e.g., Environmental remediation"
            />
            <Field
              label="Amount ($)"
              value={form.deduction_amount}
              onChange={(v) => setForm((prev) => ({ ...prev, deduction_amount: $0(v) }))}
              placeholder="e.g., 250000"
              inputMode="decimal"
            />
          </div>
        )}
        {form.deduction_type === "lease_up" && (
          <LeaseUpCalculator
            value={form.lease_up}
            onChange={(next) => setForm((prev) => ({ ...prev, lease_up: next }))}
            pgiAnnual={PGI}
            opexAnnual={EXP}
            stabilizedVacancyPct={VAC_PCT}
            defaultDiscountAnnualPct={(APPLIED_CAP || 0) * 100}
            totalBuildingSF={AREA_SF}
          />
        )}
      </div>

      {/* Value panel (now supports negative deductions visually) */}
      <ValuePanel
        deductionType={form.deduction_type}
        indicated={INDICATED_VALUE}
        manualAmount={n(form.deduction_amount)}
        leaseUp={Number(form?.lease_up?.deduction) || 0}
      />

      {/* Notes */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Notes</label>
        <textarea
          rows={4}
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
      </div>
    </div>
  );
}

// ---------- small UI ----------
function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode = "text",
  readOnly = false,
  compareKey,
}) {
  const hover = useCompareHover();

  const enableHover = !!compareKey && !!hover;

  return (
    <div
      onMouseEnter={(e) => enableHover && hover.enterIncome(e, { key: compareKey, label })}
      onMouseMove={(e) => enableHover && hover.move(e)}
      onMouseLeave={() => enableHover && hover.leave()}
    >
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        value={value ?? ""}
        onChange={(e) => !readOnly && onChange?.(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        readOnly={readOnly}
        className={[
          "w-full rounded-lg border px-3 py-2 outline-none transition",
          readOnly
            ? "bg-slate-50 border-slate-200 text-slate-500"
            : "bg-white border-gray-300 focus:border-slate-400 focus:ring-2 focus:ring-slate-200",
        ].join(" ")}
      />
    </div>
  );
}
function Toggle({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
      {options.map((opt, i) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange?.(opt.key)}
            className={[
              "px-3 py-1.5 text-sm",
              active ? "bg-slate-800 text-white" : "bg-white text-slate-700",
              i > 0 ? "border-l border-slate-300" : "",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
function Badge({ children }) {
  return <span className="inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 text-xs">{children}</span>;
}
function ValuePanel({ deductionType, indicated, manualAmount, leaseUp }) {
  const activeDeduction =
    deductionType === "manual"
      ? manualAmount
      : deductionType === "lease_up"
      ? (leaseUp || 0)
      : 0;

  const rawIndicated = Number(indicated) || 0;
  const rawAdjusted = rawIndicated - (activeDeduction || 0);
  const adjusted = Math.max(0, rawAdjusted);

  // Show adjusted card whenever there's a non-zero deduction:
  //  - positive → subtract from value
  //  - negative → add to value
  const showAdjustment =
    deductionType !== "none" && activeDeduction !== 0;

  const fmt = (v) =>
    isFinite(v) ? `$${Math.round(v).toLocaleString()}` : "—";

  if (!showAdjustment) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-xs text-slate-600">
          Indicated Value (NOI ÷ Applied Cap)
        </div>
        <div className="text-2xl font-semibold">
          {fmt(rawIndicated)}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <div className="text-xs text-emerald-700">
        Adjusted Value (Indicated − Deduction)
      </div>
      <div className="text-2xl font-semibold">
        {fmt(adjusted)}
      </div>
    </div>
  );
}

function LineItemsTable({ title, items, onChange, addHint = "Add row" }) {
  const add = () => onChange([...(items || []), { id: uid(), label: "", amount: "" }]);
  const setLabel = (i, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, label: v } : it)));
  const setAmount = (i, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, amount: $0(v) } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const total = items.reduce((s, it) => s + n(it.amount), 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="p-3 flex items-center justify-between">
        <div className="text-sm font-medium">{title}</div>
        <button
          type="button"
          onClick={add}
          className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs hover:border-slate-300"
          title={addHint}
        >
          + {addHint}
        </button>
      </div>
      <div className="px-3 pb-3 space-y-2">
        {(items || []).map((it, i) => (
          <div key={it.id || i} className="grid sm:grid-cols-[1fr_180px_40px] gap-2 items-center">
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Description"
              value={it.label || ""}
              onChange={(e) => setLabel(i, e.target.value)}
            />
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="$0"
              inputMode="decimal"
              value={it.amount || ""}
              onChange={(e) => setAmount(i, e.target.value)}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 px-2 py-2"
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}

        <div className="flex items-center justify-end">
          <div className="text-sm text-slate-600">
            Total: <span className="font-semibold">{$0(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
